import { useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  qDeliveryOrdersByPurchaseOrder,
  qPurchaseOrdersList,
  type DeliveryOrderRow,
  type PurchaseOrderRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { type ShellOutletContext } from '../../shell/shared';
import {
  InventoryListToolbar,
  InventoryPageShell,
  InventoryPrimaryAction,
  InventoryRow,
  InventoryRowActions,
  InventoryStatusBadge,
  InventoryTable,
  type InventoryStatusVariant,
} from './_shared';

const COLUMNS = [
  { label: 'PO Number', width: '160px' },
  { label: 'Date', width: '110px' },
  { label: 'Supplier', width: '1.2fr' },
  { label: 'Notes', width: '1fr' },
  { label: 'Status', width: '120px' },
  { label: 'Delivery', width: '140px' },
  { label: 'Total', width: '120px', align: 'right' as const },
  { label: 'Actions', width: '110px', align: 'right' as const },
];

function formatPeso(value: number | null): string {
  if (value == null) return '—';
  return `₱${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function poStatusVariant(status: string | null): InventoryStatusVariant {
  if (status === 'cancelled') return 'cancelled';
  if (status === 'draft') return 'draft';
  return 'completed';
}

export function PurchaseOrdersPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();

  const { data: rows = [] } = usePowerSyncQuery<PurchaseOrderRow>(qPurchaseOrdersList());

  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.po_number ?? '').toLowerCase().includes(q) ||
        (r.supplier_name ?? '').toLowerCase().includes(q) ||
        (r.notes ?? '').toLowerCase().includes(q) ||
        (r.status ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <InventoryPageShell
      title="Purchase Orders"
      subtitle="Track intended purchases from suppliers — no stock movement created on save"
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <InventoryPrimaryAction
          label="Create Order"
          onClick={() => navigate('/inventory/purchase-orders/new')}
        />
      }
    >
      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search PO number, supplier, notes, status..."
      />
      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />
      <InventoryTable
        columns={COLUMNS}
        empty={
          filtered.length === 0
            ? {
                show: true,
                title: 'No purchase orders yet.',
                hint: 'Create a PO to track intended stock orders sent to suppliers.',
              }
            : undefined
        }
      >
        {filtered.map((row) => (
          <PoListRow
            key={row.id}
            row={row}
            onReceive={() => navigate(`/inventory/purchase-orders/${row.id}/receive`)}
          />
        ))}
      </InventoryTable>
    </InventoryPageShell>
  );
}

function PoListRow({ row, onReceive }: { row: PurchaseOrderRow; onReceive: () => void }) {
  const { data: deliveries = [] } = usePowerSyncQuery<DeliveryOrderRow>(
    qDeliveryOrdersByPurchaseOrder(row.id),
  );
  const hasReceived = deliveries.some((d) => d.status === 'received');
  const hasDraft = deliveries.some((d) => d.status === 'draft');
  const deliveryLabel = hasReceived ? 'Received' : hasDraft ? 'Draft DO' : 'Not Created';

  return (
    <InventoryRow columns={COLUMNS}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {row.po_number ?? row.id.slice(0, 8)}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
        {row.po_date ?? '—'}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
        {row.supplier_name ?? '—'}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.notes ?? '—'}
      </span>
      <InventoryStatusBadge status={poStatusVariant(row.status)} />
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: hasReceived ? 'var(--color-success-text)' : 'var(--text-muted)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {deliveryLabel}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          textAlign: 'right',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {formatPeso(row.total_amount)}
      </span>
      <div style={{ display: 'flex', gap: 'var(--sp-1)', justifyContent: 'flex-end' }}>
        {!hasReceived && row.status !== 'cancelled' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReceive();
            }}
            style={{
              height: 28,
              padding: '0 8px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-primary)',
              background: 'var(--bg-surface)',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Receive
          </button>
        )}
        <InventoryRowActions />
      </div>
    </InventoryRow>
  );
}
